import { useState, useCallback } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { useEditShowroom } from "hooks/showroomHook"
import ButtonTooltip from "components/Common/ButtonTooltip"

export default function ShowroomSocialUpdate({ showroom = null }) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const { mutateAsync: editShowroom, isPending: showroomEditLoading } =
    useEditShowroom()

  // ✅ Validation schema
  const validationSchema = Yup.object({
    facebook: Yup.string().url("Invalid URL").nullable(),
    instagram: Yup.string().url("Invalid URL").nullable(),
    youtube: Yup.string().url("Invalid URL").nullable(),
    tiktok: Yup.string().url("Invalid URL").nullable(),
    linkedin: Yup.string().url("Invalid URL").nullable(),
  })

  // ✅ Submit handler
  const onSubmit = useCallback(
    async values => {
      try {
        const res = await editShowroom({ id: showroom?.id, ...values })
        toggle()
        
        toast.success(res?.message ?? "Social info updated successfully.")
      } catch (error) {
        console.error("Failed to update social info:", error)
        toast.error("Failed to update social info.")
      }
    },
    [editShowroom, showroom]
  )

  // ✅ Formik setup
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      facebook: showroom?.facebook || "",
      instagram: showroom?.instagram || "",
      youtube: showroom?.youtube || "",
      tiktok: showroom?.tiktok || "",
      linkedin: showroom?.linkedin || "",
    },
    validationSchema,
    onSubmit,
  })

  return (
    <>
      {/* ✅ Edit button */}
      <div className="d-flex justify-content-end">
        <ButtonTooltip
          type="edit"
          onClick={toggle}
          btnId="edit-showroom-social"
          btnName="Edit Social Information"
        />
      </div>

      {/* ✅ Modal */}
      <Modal isOpen={modal} toggle={toggle} centered size="lg">
        <ModalHeader toggle={toggle}>Edit Showroom Social</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row>
              <Col md={6}>
                <Label>Facebook</Label>
                <Input
                  name="facebook"
                  value={validation.values.facebook}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.facebook}
                />
                <FormFeedback>{validation.errors.facebook}</FormFeedback>
              </Col>

              <Col md={6}>
                <Label>Instagram</Label>
                <Input
                  name="instagram"
                  value={validation.values.instagram}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.instagram}
                />
                <FormFeedback>{validation.errors.instagram}</FormFeedback>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label>YouTube</Label>
                <Input
                  name="youtube"
                  value={validation.values.youtube}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.youtube}
                />
                <FormFeedback>{validation.errors.youtube}</FormFeedback>
              </Col>

              <Col md={6}>
                <Label>TikTok</Label>
                <Input
                  name="tiktok"
                  value={validation.values.tiktok}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.tiktok}
                />
                <FormFeedback>{validation.errors.tiktok}</FormFeedback>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>LinkedIn</Label>
                <Input
                  name="linkedin"
                  value={validation.values.linkedin}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.linkedin}
                />
                <FormFeedback>{validation.errors.linkedin}</FormFeedback>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button
                type="submit"
                color="success"
                disabled={showroomEditLoading}
              >
                Save Changes
                {showroomEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
